import { ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { ChevronLeft } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { useBackNavigation } from "@acto/taskpane/field-sales-2/navigation";
import { PageLoadingSkeleton } from "./PageLoadingSkeleton";

const Page = styled("main")({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
});

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[12],
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[18]}`,
  marginBottom: theme.fieldSalesTwo.spacing[24],
  borderBottom: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
}));

const BackButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.fieldSalesTwo.spacing[44],
  height: theme.fieldSalesTwo.spacing[44],
  borderRadius: theme.fieldSalesTwo.radius[20],
  border: "none",
  background: theme.fieldSalesTwo.colors.surface.glass,
  cursor: "pointer",
  transition: "background 0.15s ease",
  "&:hover": {
    background: theme.fieldSalesTwo.colors.neutral[200],
  },
  "& svg": {
    width: "20px",
    height: "20px",
  },
}));

const Title = styled("h1")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[20],
  fontWeight: theme.fieldSalesTwo.font.weight[550],
  color: theme.fieldSalesTwo.colors.neutral[900],
  margin: 0,
}));

const Content = styled("div")<{ hPadding?: "card" | "flush" }>(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `0 ${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[24]}`,
  // Aligns a page's card float with the all-todos overview (`/todos`), whose cards sit 16px from
  // the screen edge. Default (prop absent) keeps the 24px base so other consumers are unaffected.
  variants: [
    {
      // Margin-less cards (the `Card` primitive): the card sits at the page padding, so 16px here
      // puts the card edge at 16px.
      props: { hPadding: "card" },
      style: {
        paddingLeft: theme.fieldSalesTwo.spacing[16],
        paddingRight: theme.fieldSalesTwo.spacing[16],
      },
    },
    {
      // Cards that already carry their own `margin: 0 16` (e.g. TodoSection's GlassContainer): drop
      // the page padding to 0 so the card's own margin lands it at 16px, matching the overview.
      props: { hPadding: "flush" },
      style: {
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
  ],
}));

const Message = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  color: theme.fieldSalesTwo.colors.neutral[600],
}));

interface PageLayoutProps {
  title?: string;
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  /** Rendered in place of the generic "no data" message when `isEmpty`. Falls back to the default. */
  emptyState?: ReactNode;
  /** Rendered in place of the default skeleton when `isLoading`. Falls back to the default. */
  loadingState?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
  /**
   * Horizontal padding of the content area, to align cards with the all-todos overview (`/todos`,
   * cards float 16px from the edge). Omit for the default 24px.
   * - `"card"` — margin-less cards (the `Card` primitive): 16px page padding → card edge at 16px.
   * - `"flush"` — cards that add their own `margin: 0 16` (e.g. `TodoSection`): 0 page padding → 16px.
   */
  hPadding?: "card" | "flush";
  onBack?: () => void;
}

export function PageLayout({
  title,
  isLoading,
  isError,
  isEmpty,
  emptyState,
  loadingState,
  children,
  contentClassName,
  hPadding,
  onBack,
}: PageLayoutProps) {
  const t = useTranslations("fieldSalesTwo");
  const tNav = useTranslations("fieldSalesTwo.customerNav");
  const back = useBackNavigation(ActoRoutes.HOME);

  const handleBackClick = () => {
    if (onBack) {
      onBack();
      return;
    }
    back();
  };

  return (
    <Page aria-busy={isLoading || undefined}>
      {title !== undefined && (
        <Header>
          <BackButton onClick={handleBackClick} aria-label={tNav("back")}>
            <ChevronLeft />
          </BackButton>
          <Title>{title}</Title>
        </Header>
      )}
      {isLoading ? (
        (loadingState ?? <PageLoadingSkeleton />)
      ) : isError ? (
        <Message>{t("error", { defaultValue: "Something went wrong" })}</Message>
      ) : isEmpty ? (
        (emptyState ?? <Message>{t("noData", { defaultValue: "No data available" })}</Message>)
      ) : (
        <Content className={contentClassName} hPadding={hPadding}>
          {children}
        </Content>
      )}
    </Page>
  );
}

export { Page, Message };
